/**
 * Componente de debugging para ver el estado de autenticación
 * Solo visible para company_admin y super_admin
 * 
 * USO: Agregar <DebugAuth /> en cualquier página para ver los claims
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';

export function DebugAuth() {
  const { user, role, companyId, claimsLoaded, loading, userData } = useAuth();
  const [tokenClaims, setTokenClaims] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const loadTokenClaims = async () => {
      if (!user) {
        setTokenClaims(null);
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult();
        setTokenClaims(idTokenResult.claims);
      } catch (error) {
        console.error('Error loading token claims:', error);
        setTokenClaims({ error: error.message });
      }
    };

    loadTokenClaims();
  }, [user]);

  const handleForceRefresh = async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Forzando refresh de token...');
      await user.getIdToken(true);
      
      const idTokenResult = await user.getIdTokenResult(true);
      setTokenClaims(idTokenResult.claims);
      
      console.log('✅ Token refrescado:', idTokenResult.claims);
      alert('Token refrescado. Claims: ' + JSON.stringify(idTokenResult.claims));
    } catch (error) {
      console.error('❌ Error refrescando token:', error);
      alert('Error: ' + error.message);
    }
  };

  if (!user) {
    return (
      <div style={s.container}>
        <div style={s.badge}>🚫 No Auth</div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <button 
        onClick={() => setIsExpanded(!isExpanded)} 
        style={s.toggle}
        title="Toggle debug info"
      >
        🔍 {isExpanded ? 'Ocultar' : 'Debug Auth'}
      </button>

      {isExpanded && (
        <div style={s.panel}>
          <div style={s.header}>
            <h3 style={s.title}>🔍 Auth Debug Info</h3>
            <button onClick={handleForceRefresh} style={s.refreshBtn}>
              🔄 Force Refresh Token
            </button>
          </div>

          <div style={s.section}>
            <h4 style={s.sectionTitle}>👤 User Info</h4>
            <div style={s.field}>
              <span style={s.label}>Email:</span>
              <span style={s.value}>{user.email}</span>
            </div>
            <div style={s.field}>
              <span style={s.label}>UID:</span>
              <span style={s.value}>{user.uid}</span>
            </div>
            <div style={s.field}>
              <span style={s.label}>Display Name:</span>
              <span style={s.value}>{userData?.displayName || 'N/A'}</span>
            </div>
          </div>

          <div style={s.section}>
            <h4 style={s.sectionTitle}>🔐 Auth Context State</h4>
            <div style={s.field}>
              <span style={s.label}>Loading:</span>
              <span style={s.value}>{loading ? '⏳ Yes' : '✅ No'}</span>
            </div>
            <div style={s.field}>
              <span style={s.label}>Claims Loaded:</span>
              <span style={s.value}>{claimsLoaded ? '✅ Yes' : '❌ No'}</span>
            </div>
            <div style={s.field}>
              <span style={s.label}>Role (Context):</span>
              <span style={s.value}>{role || '❌ NULL'}</span>
            </div>
            <div style={s.field}>
              <span style={s.label}>Company ID (Context):</span>
              <span style={s.value}>{companyId || '❌ NULL'}</span>
            </div>
          </div>

          <div style={s.section}>
            <h4 style={s.sectionTitle}>🎫 Token Claims (directo de Firebase)</h4>
            {tokenClaims ? (
              <>
                <div style={s.field}>
                  <span style={s.label}>role:</span>
                  <span style={s.value}>{tokenClaims.role || '❌ NULL'}</span>
                </div>
                <div style={s.field}>
                  <span style={s.label}>companyId:</span>
                  <span style={s.value}>{tokenClaims.companyId || '❌ NULL'}</span>
                </div>
                <div style={s.field}>
                  <span style={s.label}>All Claims:</span>
                  <pre style={s.code}>{JSON.stringify(tokenClaims, null, 2)}</pre>
                </div>
              </>
            ) : (
              <div style={s.value}>⏳ Loading...</div>
            )}
          </div>

          <div style={s.section}>
            <h4 style={s.sectionTitle}>📊 Diagnóstico</h4>
            {!claimsLoaded && <div style={s.error}>⚠️ Claims no cargados aún</div>}
            {claimsLoaded && !role && <div style={s.error}>❌ Role es NULL (PROBLEMA)</div>}
            {claimsLoaded && role && !tokenClaims?.role && (
              <div style={s.error}>❌ Role en context pero NO en token (PROBLEMA)</div>
            )}
            {claimsLoaded && role && tokenClaims?.role && role !== tokenClaims.role && (
              <div style={s.error}>❌ Role mismatch: Context={role}, Token={tokenClaims.role}</div>
            )}
            {claimsLoaded && role && tokenClaims?.role && role === tokenClaims.role && (
              <div style={s.success}>✅ Todo correcto - Claims cargados correctamente</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  container: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 9999,
  },
  toggle: {
    padding: '10px 16px',
    background: '#1e293b',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    transition: 'all 0.2s',
  },
  panel: {
    position: 'absolute',
    bottom: 50,
    right: 0,
    width: 500,
    maxHeight: '80vh',
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    padding: 20,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '2px solid #e2e8f0',
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: '#0f172a',
  },
  refreshBtn: {
    padding: '6px 12px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  },
  section: {
    marginBottom: 16,
    padding: 12,
    background: '#f8fafc',
    borderRadius: 8,
  },
  sectionTitle: {
    margin: '0 0 8px 0',
    fontSize: 13,
    fontWeight: 700,
    color: '#475569',
  },
  field: {
    display: 'flex',
    marginBottom: 6,
    fontSize: 12,
  },
  label: {
    fontWeight: 600,
    color: '#64748b',
    minWidth: 140,
  },
  value: {
    color: '#0f172a',
    wordBreak: 'break-all',
  },
  code: {
    background: '#1e293b',
    color: '#e2e8f0',
    padding: 8,
    borderRadius: 4,
    fontSize: 11,
    overflow: 'auto',
    maxHeight: 200,
    margin: '8px 0 0 0',
  },
  error: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: 8,
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    marginTop: 8,
  },
  success: {
    background: '#dcfce7',
    color: '#166534',
    padding: 8,
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    marginTop: 8,
  },
  badge: {
    padding: '8px 12px',
    background: '#ef4444',
    color: '#fff',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
};

export default DebugAuth;
